/**
 * Polling-based data hook (Cloudflare Workers free tier).
 * Poll interval scales with timeframe — higher TF = less frequent signal polls.
 * Only 90%+ confidence signals are returned by the Worker.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
const API_BASE = import.meta.env.VITE_API_URL ?? '';
// How often to poll for a new signal per timeframe (ms)
const SIGNAL_POLL = {
    M5: 30_000, // 30 s
    M15: 60_000, // 1 min
    M30: 120_000, // 2 min
    H1: 300_000, // 5 min
    H4: 600_000, // 10 min
    D1: 1_800_000, // 30 min
};
const PRICE_POLL = 3_000; // price ticks every 3 s
const MARKET_POLL = 15_000; // market data every 15 s
const ALERT_POLL = 30_000; // liquidity alerts every 30 s
async function apiFetch(path) {
    const res = await fetch(`${API_BASE}${path}`);
    if (!res.ok)
        throw new Error(`HTTP ${res.status}`);
    return res.json();
}
export function useWebSocket(onMessage, timeframe = 'M15') {
    const [status, setStatus] = useState('connecting');
    const [lastPing, setLastPing] = useState(null);
    const mountedRef = useRef(true);
    const timersRef = useRef([]);
    const tfRef = useRef(timeframe);
    tfRef.current = timeframe;
    const poll = useCallback(async (path, type) => {
        if (!mountedRef.current)
            return;
        try {
            const data = await apiFetch(path);
            if (!mountedRef.current)
                return;
            setStatus('connected');
            setLastPing(Date.now());
            // Signal endpoint: { found: false } means no valid setup right now — skip
            if (type === 'signal' && data && typeof data === 'object' && 'found' in data) {
                if (!data.found)
                    return;
                // Strip the `found` wrapper before dispatching
                const { found: _found, ...signal } = data;
                onMessage({ type, data: signal, timestamp: Date.now() });
                return;
            }
            // /api/price returns PriceUpdate[] — dispatch each individually
            if (Array.isArray(data)) {
                for (const item of data) {
                    onMessage({ type, data: item, timestamp: Date.now() });
                }
            }
            else {
                onMessage({ type, data, timestamp: Date.now() });
            }
        }
        catch {
            if (mountedRef.current)
                setStatus('error');
        }
    }, [onMessage]);
    useEffect(() => {
        mountedRef.current = true;
        timersRef.current.forEach(clearInterval);
        timersRef.current = [];
        const signalPath = () => `/api/signal?tf=${tfRef.current}`;
        // Immediate first fetch
        poll(signalPath(), 'signal');
        poll('/api/market', 'market_data');
        poll('/api/price', 'price_update');
        timersRef.current.push(setInterval(() => poll(signalPath(), 'signal'), SIGNAL_POLL[timeframe]), setInterval(() => poll('/api/price', 'price_update'), PRICE_POLL), setInterval(() => poll('/api/market', 'market_data'), MARKET_POLL), setInterval(() => poll('/api/alert', 'liquidity_alert'), ALERT_POLL));
        return () => {
            mountedRef.current = false;
            timersRef.current.forEach(clearInterval);
        };
        // Re-run when timeframe changes so poll interval resets
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeframe, poll]);
    const send = useCallback((_msg) => { }, []);
    return { status, lastPing, send };
}
